import {CommentIcon} from '@primer/octicons-react'
import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {PreviewAuthors} from '../../../toolbar/comments-panel/PreviewAuthors'
import {ProfileReference} from '@github-ui/profile-reference'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {Link} from '@primer/react'
import type {ActionSeverity} from './ActionF'
import styles from './CommentActionHeader.module.css'
import {clsx} from 'clsx'

type CommentActionHeaderProps = {
  authorLogin: string | undefined
  filePath: string | undefined
  thread: Thread
  severity: ActionSeverity
}

function FileName({path}: {path: string}) {
  return (
    <span className={styles.fileName}>
      <code>&lrm;{path}&lrm;</code>
    </span>
  )
}

export const CommentActionHeader = ({authorLogin, filePath, thread, severity}: CommentActionHeaderProps) => {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const isMuted = severity === 'low'

  const iconColorClass = isMuted ? 'color-fg-muted' : 'color-fg-accent'

  if (thread.isResolved) {
    return (
      <div className={styles.headerRow}>
        <span className={clsx(styles.iconWrapper, iconColorClass)}>
          <CommentIcon size="small" aria-hidden="true" />
          {thread.commentsData?.comments.length ? (
            <span
              role="img"
              className={clsx(styles.badge, styles.badgeMuted)}
              aria-label={`${thread.commentsData.comments.length} comments`}
            >
              {thread.commentsData.comments.length}
            </span>
          ) : null}
        </span>
        <PreviewAuthors thread={thread} />
        <span className="color-fg-muted flex-shrink-0">Resolved</span>
        {filePath && <FileName path={filePath} />}
      </div>
    )
  }

  return (
    <div className={styles.headerRow}>
      <span className={clsx(styles.iconWrapper, iconColorClass)}>
        <CommentIcon size="small" aria-hidden="true" />
        {thread.commentsData?.comments.length ? (
          <span
            role="img"
            className={clsx(styles.badge, isMuted ? styles.badgeMuted : styles.badgeActive)}
            aria-label={`${thread.commentsData.comments.length} comments`}
          >
            {thread.commentsData.comments.length}
          </span>
        ) : null}
      </span>
      <div className={styles.innerRow}>
        <PreviewAuthors thread={thread} />
        {authorLogin && (
          <span className={styles.noWrap}>
            <Link
              href={`/${authorLogin}`}
              className="color-fg-default text-bold"
              {...getHovercardAttributesForType('user', {login: authorLogin})}
            >
              <ProfileReference login={authorLogin} isAgent={false} />
            </Link>{' '}
            replied to
          </span>
        )}
        {filePath && <FileName path={filePath} />}
      </div>
    </div>
  )
}
